import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import {
  baseI18nOptions,
  dirForLocale,
  LOCALE_STORAGE_KEY,
  loadLocaleBundle,
  SUPPORTED_LOCALES,
  type SupportedLocale,
} from '@kidshome/i18n';

/**
 * react-i18next init (ADR-0025). `en` is bundled and initialized synchronously; any other persisted/
 * detected locale ships as a lazy chunk, so the entry point awaits `localeReady` (capped — see below)
 * before the first React render to avoid an English flash for returning non-`en` users. Locale
 * precedence: persisted override (localStorage) -> navigator language -> fallback `en`. `<html lang>`
 * + `dir` are kept in sync on every language change.
 */
void i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    ...baseI18nOptions,
    react: { useSuspense: false },
  });

function syncDocumentLang(locale: string): void {
  const root = document.documentElement;
  root.lang = locale;
  root.dir = dirForLocale(locale);
}

syncDocumentLang(i18n.resolvedLanguage ?? 'en');
i18n.on('languageChanged', syncDocumentLang);

function isSupported(locale: string): locale is SupportedLocale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(locale);
}

/**
 * Ensure a locale's catalogs are loaded (lazy per-locale chunk; `en` is bundled). Adds each
 * namespace bundle to i18next before the language is activated.
 */
async function ensureLocaleLoaded(locale: SupportedLocale): Promise<void> {
  if (locale === 'en' || i18n.hasResourceBundle(locale, 'common')) {
    return;
  }
  const bundle = await loadLocaleBundle(locale);
  for (const [ns, catalog] of Object.entries(bundle)) {
    i18n.addResourceBundle(locale, ns, catalog, true, true);
  }
}

// If the detected/persisted locale isn't the bundled one, fetch its chunk on startup. The entry
// point awaits `localeReady` before the first render so a returning `uk` user paints in Ukrainian
// (not an English flash that flips mid-view). The wait is CAPPED: on a very slow connection we
// render in `en` after the cap and the language still flips when the chunk lands (the old behavior
// as a fallback, never an indefinitely blank screen).
const LOCALE_READY_CAP_MS = 2_500;
const resolved = i18n.resolvedLanguage ?? 'en';

/**
 * Tracks the most recently REQUESTED locale (the startup load below counts as the first request).
 * Startup and every `setLocale` call each lazy-load a chunk and race those loads against each other;
 * each records itself here before awaiting so that if a newer request supersedes an earlier,
 * still-pending one, the earlier one's `i18n.changeLanguage` is skipped when it finally resolves
 * instead of clobbering the user's latest choice — otherwise two rapid switches apply in whichever
 * order their imports happen to settle, not the order they were requested in.
 */
let latestRequestedLocale: string = resolved;

/** Load `locale`'s catalogs (if needed) and activate it — unless a newer locale was requested meanwhile. */
function applyLocale(locale: SupportedLocale): Promise<void> {
  latestRequestedLocale = locale;
  return ensureLocaleLoaded(locale).then(() => {
    if (latestRequestedLocale !== locale) {
      return; // superseded by a later request while this chunk was loading — don't apply it.
    }
    return i18n.changeLanguage(locale).then(() => undefined);
  });
}

/** Resolves when the startup locale's catalogs are active (or the cap elapsed / loading failed). */
export const localeReady: Promise<void> =
  isSupported(resolved) && resolved !== 'en'
    ? Promise.race([
        applyLocale(resolved),
        new Promise((timeUp) => setTimeout(timeUp, LOCALE_READY_CAP_MS)),
      ]).then(
        () => undefined,
        () => undefined, // chunk failed → render in `en`; setLocale can retry later
      )
    : Promise.resolve();

/** Change the active locale (lazy-loading its chunk) and persist it (mirrored to `User.locale`). */
export function setLocale(locale: string): void {
  const next = isSupported(locale) ? locale : 'en';
  void applyLocale(next);
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, next);
  } catch {
    // localStorage unavailable (private mode) — language still applies for the session.
  }
}

export default i18n;
